import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import { useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import Image from 'next/image';
import Link from 'next/link';
import React, { useMemo, useState } from 'react';
import { Images } from '@/assets/images';
import RenderIf from './RenderIf';

export interface NFTDetails {
  token_id: string;
  title: string;
  description: string;
  price: string | number;
  image: string;
  owner?: string;
  network: string;
  token_type: string;
  contract_Address: string;
  collection_Name: string;
  metadata_url: string;
  animation_url?: string;
  quantity?: number;
  traits: Trait[];
  saleId: string;
  isSoldOut?: boolean;
  creation_date?: string;
}

export interface Trait {
  trait_type: string;
  value: string;
  percentage?: string;
}

interface NFTCardProps {
  data: NFTDetails;
  conversionRate?: number | string;
  isSoldOut?: boolean;
}

const NFTCard: React.FC<NFTCardProps> = ({ data, conversionRate, isSoldOut }) => {
  const theme = useTheme();
  const { contract_Address, token_id } = data;

  const [imgSrc, setImgSrc] = useState(data?.image || Images.CARD_IMG);
  const [imageLoading, setImageLoading] = useState(true);

  // Check if image is already loaded or cached
  React.useEffect(() => {
    const img = new window.Image();
    img.onload = () => setImageLoading(false);
    img.onerror = () => {
      setImgSrc(Images.CARD_IMG);
      setImageLoading(false);
    };
    img.src = data?.image || Images.CARD_IMG;
  }, [data?.image]);

  const renderButtonName = useMemo(() => {
    if (isSoldOut) {
      return 'View Details';
    }

    return 'View Details and Buy';
  }, [isSoldOut]);

  return (
    <Box
      sx={{
        backgroundColor: '#18181B',
        borderRadius: 2,
        transition: 'transform 0.3s, box-shadow 0.3s',
        position: 'relative',
        border: `1px solid ${theme?.palette?.text?.secondary}`,
        '&:hover': {
          transform: 'scale(1.03)',
          boxShadow: `0 2px 10px rgba(0, 0, 0, 0.3)`
        }
      }}
    >
      <Stack
        component="button"
        height="258px"
        bgcolor="#000"
        alignItems="center"
        borderRadius="8px 8px 0 0"
        overflow="hidden"
        justifyContent="center"
        sx={{ cursor: 'pointer', width: '100%' }}
      >
        <Link href={`/${contract_Address}/${token_id}`} prefetch passHref style={{ width: '100%' }}>
          {imageLoading && (
            <Box
              sx={{
                width: '100%',
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: '#1c2833'
              }}
            >
              <Box
                component="div"
                sx={{
                  width: '100%',
                  height: '144px',
                  backgroundColor: '#2c3e50',
                  borderRadius: '4px',
                  animation: 'pulse 1.5s ease-in-out infinite',
                  '@keyframes pulse': {
                    '0%': { opacity: 0.6 },
                    '50%': { opacity: 0.3 },
                    '100%': { opacity: 0.6 }
                  }
                }}
              />
            </Box>
          )}
          <Image
            src={imgSrc}
            onError={() => setImgSrc(Images.CARD_IMG)}
            alt={data?.title || 'card'}
            placeholder="blur"
            blurDataURL="data:image/jpeg;base64,/9j/4AAQSkZJRgABAQAAAQABAAD/2wBDAAYEBQYFBAYGBQYHBwYIChAKCgkJChQODwwQFxQYGBcUFhYaHSUfGhsjHBYWICwgIyYnKSopGR8tMC0oMCUoKSj/2wBDAQcHBwoIChMKChMoGhYaKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCj/wAARCAABAAEDASIAAhEBAxEB/8QAFQABAQAAAAAAAAAAAAAAAAAAAAv/xAAUEAEAAAAAAAAAAAAAAAAAAAAA/8QAFQEBAQAAAAAAAAAAAAAAAAAAAAX/xAAUEQEAAAAAAAAAAAAAAAAAAAAA/9oADAMBAAIRAxEAPwCdABmX/9k="
            loading="lazy"
            width={256}
            height={148}
            style={{
              objectFit: 'contain',
              height: '100%',
              width: '100%',
              display: imageLoading ? 'none' : 'block'
            }}
          />
        </Link>
      </Stack>
      <Box sx={{ p: 1 }}>
        <Typography
          variant="h5"
          marginBottom={2}
          sx={{
            fontFamily: theme?.MojitoClaim?.font?.primary,
            fontWeight: 500,
            fontSize: '24px',
            lineHeight: '28.8px',
            color: theme.palette.text.primary,
            textAlign: { xs: 'left', sm: 'left', md: 'left' },
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis'
          }}
        >
          {data.title}
        </Typography>
        <Typography
          style={{
            color: theme.palette.text.primary,
            fontWeight: 500,
            fontFamily: theme?.MojitoClaim?.font?.secondary,
            lineHeight: isSoldOut ? '22px' : '28.8px',
            fontSize: '24px',
            fontStyle: 'normal',
            visibility: isSoldOut ? 'hidden' : 'visible'
          }}
        >
          {Number(data.price).toFixed(4) + ' ETH'}
        </Typography>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <RenderIf condition={!!isSoldOut}>
            <Typography
              style={{
                color: theme.palette.text.primary,
                fontWeight: 500,
                fontFamily: theme?.MojitoClaim?.font?.secondary,
                lineHeight: '28.8px',
                fontSize: '24px',
                fontStyle: 'normal',
                visibility: 'visible'
              }}
            >
              SOLD
            </Typography>
            <RenderIf.Else>
              <Typography
                sx={{
                  fontFamily: theme?.MojitoClaim?.font?.secondary,
                  color: theme.ClaimTemplate?.collectionHeaderColor,
                  textAlign: { xs: 'center', sm: 'center', md: 'left' },
                  visibility: 'visible'
                }}
              >
                {Number(conversionRate) ? (Number(data.price) * Number(conversionRate)).toFixed(2) + ' USD' : '-'}
              </Typography>
            </RenderIf.Else>
          </RenderIf>

          <Typography
            sx={{
              fontSize: '10px',
              color: theme.palette.text.primary,
              padding: `4px 6px`,
              borderRadius: '10px',
              backgroundColor: '#232323'
            }}
          >
            {data?.token_type}
          </Typography>
        </Box>
        <Stack spacing={2} mt={2}>
          <Link href={`/${contract_Address}/${token_id}`} prefetch passHref>
            <Button
              variant="contained"
              fullWidth
              sx={{
                textTransform: 'none',
                backgroundColor: 'transparent',
                border: `2px solid ${theme?.palette?.text?.secondary}`,
                color: '#fff',
                '&:disabled': {
                  color: theme.palette.background.paper
                },
                '&:hover': {
                  backgroundColor: 'transparent',
                  color: '#fff'
                }
              }}
            >
              {renderButtonName}
            </Button>
          </Link>
        </Stack>
      </Box>
    </Box>
  );
};

export default NFTCard;
